import {
  whitehavenWellnessStarterTemplate,
  type CmsProvisioningDraft,
  type CmsProvisioningPlan,
  type CmsTemplateBundle,
} from "@whitehaven/contracts/cms-product";
import type { HomepageContent } from "@whitehaven/contracts/homepage";
import { parseHomepageContent } from "./homepage";

const identifierPattern = /^[a-z0-9][a-z0-9_-]{2,95}$/u;
const hostnamePattern =
  /^(?=.{3,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/u;
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;
const internalPathPattern = /^\/[A-Za-z0-9/_-]*$/u;
const assetSourcePattern =
  /^(?:\/media\/[A-Za-z0-9/_.-]+|https:\/\/[A-Za-z0-9.-]+\/[A-Za-z0-9/?&=_.%-]+)$/u;
const supportedRoles = new Set([
  "OWNER",
  "ADMIN",
  "SCHEDULER",
  "FRONT_DESK",
  "STAFF",
  "CRM_AGENT",
  "GUEST",
]);

const approvedTemplates: Readonly<Record<string, CmsTemplateBundle>> = {
  [whitehavenWellnessStarterTemplate.templateId]:
    whitehavenWellnessStarterTemplate,
};

export class CmsProvisioningValidationError extends Error {
  readonly issues: readonly string[];

  constructor(issues: readonly string[]) {
    super("The site setup plan is incomplete or unsafe.");
    this.name = "CmsProvisioningValidationError";
    this.issues = issues;
  }
}

function bounded(
  value: string,
  label: string,
  maximum: number,
  issues: string[],
) {
  const normalized = value.trim().normalize("NFC");
  if (normalized.length < 2 || normalized.length > maximum) {
    issues.push(`${label} must contain 2–${maximum} characters.`);
  }
  return normalized;
}

function substituteTemplateStrings(
  value: unknown,
  replacements: readonly (readonly [string, string])[],
): unknown {
  if (typeof value === "string") {
    return replacements.reduce(
      (current, [search, replacement]) =>
        current.replaceAll(search, replacement),
      value,
    );
  }
  if (Array.isArray(value)) {
    return value.map((item) => substituteTemplateStrings(item, replacements));
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [
        key,
        substituteTemplateStrings(item, replacements),
      ]),
    );
  }
  return value;
}

export function approvedCmsTemplates(): readonly CmsTemplateBundle[] {
  return Object.values(approvedTemplates);
}

/**
 * Builds a deterministic, portable dry-run. It performs no database, account,
 * DNS, asset, invitation, or publish operation.
 */
export function buildCmsProvisioningPlan(
  draft: CmsProvisioningDraft,
): CmsProvisioningPlan {
  const issues: string[] = [];
  const template = approvedTemplates[draft.templateId];
  if (!template) issues.push("Choose an approved starter template.");
  const organizationId = draft.organizationId.trim().toLowerCase();
  const siteId = draft.siteId.trim().toLowerCase();
  if (!identifierPattern.test(organizationId)) {
    issues.push("Organization ID must use lowercase letters, numbers, _ or -.");
  }
  if (!identifierPattern.test(siteId)) {
    issues.push("Site ID must use lowercase letters, numbers, _ or -.");
  }
  const siteName = bounded(draft.siteName, "Site name", 120, issues);
  const organizationName = bounded(
    draft.organizationName,
    "Organization name",
    120,
    issues,
  );
  const legalName = bounded(draft.legalName, "Legal name", 160, issues);
  const locality = bounded(draft.locality, "Locality", 100, issues);
  const region = bounded(draft.region, "Region", 100, issues);
  const primaryDomain = draft.primaryDomain.trim().toLowerCase();
  if (!hostnamePattern.test(primaryDomain)) {
    issues.push("Primary domain must be a hostname without a scheme or path.");
  }
  const contactEmail = draft.contactEmail.trim().toLowerCase();
  if (!emailPattern.test(contactEmail))
    issues.push("Contact email is invalid.");
  if (!/^[A-Z]{2}$/u.test(draft.countryCode)) {
    issues.push("Country code must be a two-letter uppercase code.");
  }
  for (const [label, path] of [
    ["Privacy policy path", draft.privacyPolicyPath],
    ["Terms path", draft.termsPath],
  ] as const) {
    if (path !== null && !internalPathPattern.test(path)) {
      issues.push(`${label} must be a same-site absolute path.`);
    }
  }
  const assetIds = new Set<string>();
  for (const asset of draft.assets) {
    if (!identifierPattern.test(asset.id) || assetIds.has(asset.id)) {
      issues.push("Brand asset IDs must be unique safe identifiers.");
    }
    assetIds.add(asset.id);
    if (!asset.altText.trim() || asset.altText.length > 240) {
      issues.push(`Asset ${asset.id} needs concise alternative text.`);
    }
    if (!assetSourcePattern.test(asset.source)) {
      issues.push(`Asset ${asset.id} must use /media/ or an HTTPS URL.`);
    }
    if (
      !Number.isInteger(asset.width) ||
      !Number.isInteger(asset.height) ||
      asset.width < 1 ||
      asset.height < 1 ||
      asset.width > 8_192 ||
      asset.height > 8_192
    ) {
      issues.push(`Asset ${asset.id} dimensions are invalid.`);
    }
  }
  const userEmails = new Set<string>();
  for (const user of draft.initialUsers) {
    const email = user.email.trim().toLowerCase();
    if (!emailPattern.test(email) || userEmails.has(email)) {
      issues.push("Initial user emails must be valid and unique.");
    }
    userEmails.add(email);
    if (!supportedRoles.has(user.roleKey)) {
      issues.push(`Initial user ${email} has an unsupported role.`);
    }
  }
  for (const resource of draft.initialResources) {
    bounded(resource.name, "Resource name", 120, issues);
    bounded(resource.description, "Resource description", 500, issues);
  }
  if (!template || issues.length > 0) {
    throw new CmsProvisioningValidationError(issues);
  }

  const replacements = [
    ["Whitehaven Wellness Hub", siteName],
    ["Whitehaven Wellness", organizationName],
    ["Whitehaven", organizationName],
    ["whitehavenwellness.com", primaryDomain],
    ["Hilo", locality],
  ] as const;
  const substituted = substituteTemplateStrings(
    structuredClone(template.homepage),
    replacements,
  ) as HomepageContent;
  const homepage = parseHomepageContent({
    ...substituted,
    theme: draft.themePreset,
    contact: { ...substituted.contact, email: contactEmail },
    footer: {
      ...substituted.footer,
      legalLine: draft.emergencyNotice.trim(),
    },
  });
  const profile = {
    ...template.profileDefaults,
    siteId,
    organizationId,
    siteName,
    organizationName,
    legalName,
    primaryDomain,
    contactEmail,
    contactPhone: draft.contactPhone?.trim() || null,
    locality,
    region,
    countryCode: draft.countryCode,
    privacyPolicyPath: draft.privacyPolicyPath,
    termsPath: draft.termsPath,
    emergencyNotice: draft.emergencyNotice.trim(),
    theme: draft.themePreset,
    assets: structuredClone(draft.assets),
    seoDefaults: {
      ...template.profileDefaults.seoDefaults,
      titleTemplate: `%s — ${siteName}`,
    },
  };

  return {
    kind: "cms-provisioning-dry-run",
    planVersion: 1,
    template: { id: template.templateId, version: template.templateVersion },
    profile,
    homepage,
    navigation: structuredClone(template.navigation),
    globalBlocks: structuredClone(template.globalBlocks),
    redirects: structuredClone(template.redirects),
    requiredContentReviewPaths: template.requiredContentReviewPaths,
    initialUsers: structuredClone(draft.initialUsers),
    initialResources: structuredClone(draft.initialResources),
    requiresConfirmation: [
      "create-tenant-records",
      "invite-users",
      "configure-domain",
      "provision-external-resources",
      "publish-content",
    ],
  };
}
